import React, { useCallback, useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import Axios from 'axios';
import { API_ROUTES } from 'src/config/api';
import { Spinner } from 'reactstrap';
import Skeleton from 'react-loading-skeleton';
import { Plus, Trash2, AlertCircle } from 'lucide-react';

import 'react-loading-skeleton/dist/skeleton.css';

// Default empty tier template
const createEmptyTier = () => ({
  min_value: '',
  max_value: '',
  rate: '',
});

const BillingSettings = ({ organisation }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    variable_rate: '',
    surcharge_fee: '',
    open_banking_fee: '',
    open_banking_cap: '',
    monthly_fee: '',
    bill_child_accounts: false,
  });
  const [variableRateTiers, setVariableRateTiers] = useState([]);
  const [useTieredRates, setUseTieredRates] = useState(false);

  const fetchBillingSettings = useCallback(async () => {
    setLoading(true);

    const user = await Auth.currentAuthenticatedUser();

    try {
      const result = await Axios(
        API_ROUTES.user['organisation:billing'](organisation.organisation),
        {
          method: 'get',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );

      if (result.data.data) {
        setFormData({
          variable_rate: result.data.data.variable_rate ?? '',
          surcharge_fee: result.data.data.surcharge_fee ?? '',
          open_banking_fee: result.data.data.open_banking_fee ?? '',
          open_banking_cap: result.data.data.open_banking_cap ?? '',
          monthly_fee: result.data.data.monthly_fee ?? '',
          bill_child_accounts: result.data.data.bill_child_accounts ?? false,
        });

        // Load tiered rates if they exist
        if (result.data.data.variable_rate_tiers && result.data.data.variable_rate_tiers.length > 0) {
          setVariableRateTiers(result.data.data.variable_rate_tiers.map(tier => ({
            min_value: tier.min_value?.toString() ?? '',
            max_value: tier.max_value?.toString() ?? '',
            rate: tier.rate?.toString() ?? '',
          })));
          setUseTieredRates(true);
        }
      }
    } catch (error) {
      // If no billing settings exist yet, that's okay - we'll use empty defaults
      console.log('No existing billing settings found');
    }

    setLoading(false);
  }, [organisation.organisation]);

  useEffect(() => {
    fetchBillingSettings();
  }, [fetchBillingSettings]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    const user = await Auth.currentAuthenticatedUser();

    // Parse tiered rates for API
    const parsedTiers = useTieredRates
      ? variableRateTiers
          .filter(tier => tier.min_value !== '' && tier.max_value !== '' && tier.rate !== '')
          .map(tier => ({
            min_value: parseFloat(tier.min_value),
            max_value: parseFloat(tier.max_value),
            rate: parseFloat(tier.rate),
          }))
      : [];

    try {
      await Axios.put(
        API_ROUTES.user['organisation:billing'](organisation.organisation),
        {
          // If using tiered rates, don't send the flat variable_rate
          variable_rate: useTieredRates ? null : (formData.variable_rate === '' ? null : parseFloat(formData.variable_rate)),
          variable_rate_tiers: parsedTiers.length > 0 ? parsedTiers : null,
          surcharge_fee: formData.surcharge_fee === '' ? null : parseFloat(formData.surcharge_fee),
          open_banking_fee: formData.open_banking_fee === '' ? null : parseFloat(formData.open_banking_fee),
          open_banking_cap: formData.open_banking_cap === '' ? null : parseFloat(formData.open_banking_cap),
          monthly_fee: formData.monthly_fee === '' ? null : parseFloat(formData.monthly_fee),
          bill_child_accounts: formData.bill_child_accounts,
        },
        {
          headers: {
            Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      await fetchBillingSettings();
    } catch (error) {
      console.error('Error saving billing settings:', error);
    }

    setSaving(false);
  };

  // Tier management functions
  const addTier = () => {
    const lastTier = variableRateTiers[variableRateTiers.length - 1];
    const newTier = createEmptyTier();

    // Auto-fill min_value based on previous tier's max_value
    if (lastTier && lastTier.max_value) {
      newTier.min_value = lastTier.max_value;
    }

    setVariableRateTiers([...variableRateTiers, newTier]);
  };

  const removeTier = (index) => {
    setVariableRateTiers(variableRateTiers.filter((_, i) => i !== index));
  };

  const updateTier = (index, field, value) => {
    const updatedTiers = [...variableRateTiers];
    updatedTiers[index] = { ...updatedTiers[index], [field]: value };

    // Auto-update next tier's min_value when max_value changes
    if (field === 'max_value' && index < updatedTiers.length - 1) {
      updatedTiers[index + 1] = { ...updatedTiers[index + 1], min_value: value };
    }

    setVariableRateTiers(updatedTiers);
  };

  const toggleTieredRates = (enabled) => {
    setUseTieredRates(enabled);
    if (enabled && variableRateTiers.length === 0) {
      // Add initial tier starting from 0
      setVariableRateTiers([{ min_value: '0', max_value: '', rate: '' }]);
    }
  };

  const inputFields = [
    {
      key: 'surcharge_fee',
      label: 'Surcharge Fee',
      description: 'Percentage fee on surcharging',
      placeholder: 'e.g. 0.5',
      suffix: '%',
    },
    {
      key: 'open_banking_fee',
      label: 'Open Banking Fee',
      description: 'Percentage fee per transaction on open banking',
      placeholder: 'e.g. 0.3',
      suffix: '%',
    },
    {
      key: 'open_banking_cap',
      label: 'Open Banking Cap',
      description: 'Maximum price per transaction on open banking',
      placeholder: 'e.g. 5.00',
      prefix: '£',
    },
    {
      key: 'monthly_fee',
      label: 'Monthly Fee',
      description: 'Flat fee charged per month',
      placeholder: 'e.g. 100.00',
      prefix: '£',
    },
  ];

  // Format currency for display in tiers
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 border-b border-violet-900 pb-2">
        <h3 className="font-extralight uppercase font-mono text-2xl dark:text-white text-violet-900">
          Billing Configuration
        </h3>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          className="btn btn-md btn-primary bg-violet-800 hover:bg-violet-900 text-white px-6 py-2 rounded-md -mt-3"
        >
          {saving ? (
            <Spinner size="sm">{''}</Spinner>
          ) : (
            'Save Configuration'
          )}
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Configure bespoke billing settings for this organisation. These settings will be used for automated billing calculations.
      </p>

      <div className="clearfix h-auto">
        {loading === true && (
          <div className="mt-2 mb-2">
            <Skeleton count={5} height="70px" className="mt-1 mb-1" style={{ width: '100%' }} />
          </div>
        )}

        {loading === false && (
          <div>
            {/* Variable Rate Section */}
                        {/* Child Account Billing Section */}
            <div className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-purple-900 text-lg">Child Account Billing</h4>
                  <p className="text-sm text-gray-500">
                    When enabled, child accounts under this organisation will be billed from this account instead of being billed individually.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${!formData.bill_child_accounts ? 'font-semibold text-purple-900' : 'text-gray-500'}`}>
                    Bill Individually
                  </span>
                  <button
                    type="button"
                    onClick={() => handleInputChange('bill_child_accounts', !formData.bill_child_accounts)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.bill_child_accounts ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.bill_child_accounts ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm ${formData.bill_child_accounts ? 'font-semibold text-purple-900' : 'text-gray-500'}`}>
                    Bill From This Account
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-bold text-purple-900 text-lg">Variable Rate</h4>
                  <p className="text-sm text-gray-500">Metered pricing based on transaction volume per month</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${!useTieredRates ? 'font-semibold text-purple-900' : 'text-gray-500'}`}>
                    Flat Rate
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleTieredRates(!useTieredRates)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      useTieredRates ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        useTieredRates ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm ${useTieredRates ? 'font-semibold text-purple-900' : 'text-gray-500'}`}>
                    Tiered Rates
                  </span>
                </div>
              </div>

              {/* Flat Rate Input */}
              {!useTieredRates && (
                <div className="max-w-xs">
                  <div className="flex items-stretch">
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      className="border border-gray-300 px-3 py-2 w-full rounded-l-md"
                      placeholder="e.g. 0.5"
                      value={formData.variable_rate}
                      onChange={(e) => handleInputChange('variable_rate', e.target.value)}
                    />
                    <span className="bg-gray-100 border border-l-0 border-gray-300 px-3 flex items-center rounded-r-md text-gray-600">
                      %
                    </span>
                  </div>
                </div>
              )}

              {/* Tiered Rates UI */}
              {useTieredRates && (
                <div style={{ position: 'relative' }}>
                  {/* Header Row */}
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left text-sm font-semibold text-gray-600 pb-3 w-[35%]">From Value</th>
                        <th className="text-left text-sm font-semibold text-gray-600 pb-3 w-[35%]">To Value</th>
                        <th className="text-left text-sm font-semibold text-gray-600 pb-3 w-[20%]">Rate</th>
                        <th className="w-[10%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {variableRateTiers.map((tier, index) => (
                        <tr key={index}>
                          {/* Min Value */}
                          <td className="pb-3 pr-3">
                            <div className="flex items-stretch">
                              <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 flex items-center rounded-l-md text-gray-600 text-sm">
                                £
                              </span>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                className="border border-gray-300 px-3 py-2 w-full rounded-r-md bg-gray-50"
                                placeholder="0"
                                value={tier.min_value}
                                onChange={(e) => updateTier(index, 'min_value', e.target.value)}
                                disabled={index > 0}
                              />
                            </div>
                          </td>

                          {/* Max Value */}
                          <td className="pb-3 pr-3">
                            <div className="flex items-stretch">
                              <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 flex items-center rounded-l-md text-gray-600 text-sm">
                                £
                              </span>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                className="border border-gray-300 px-3 py-2 w-full rounded-r-md"
                                placeholder="e.g. 2500000"
                                value={tier.max_value}
                                onChange={(e) => updateTier(index, 'max_value', e.target.value)}
                              />
                            </div>
                          </td>

                          {/* Rate */}
                          <td className="pb-3 pr-3">
                            <div className="flex items-stretch">
                              <input
                                type="number"
                                step="0.001"
                                min="0"
                                className="border border-gray-300 px-3 py-2 w-full rounded-l-md"
                                placeholder="e.g. 0.2"
                                value={tier.rate}
                                onChange={(e) => updateTier(index, 'rate', e.target.value)}
                              />
                              <span className="bg-gray-100 border border-l-0 border-gray-300 px-3 flex items-center rounded-r-md text-gray-600 text-sm">
                                %
                              </span>
                            </div>
                          </td>

                          {/* Delete Button */}
                          <td className="pb-3 text-center">
                            {variableRateTiers.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTier(index)}
                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Add Tier Button */}
                  <button
                    type="button"
                    onClick={addTier}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors mt-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Tier
                  </button>

                  {/* Preview/Summary */}
                  {variableRateTiers.length > 0 && variableRateTiers.some(t => t.min_value && t.max_value && t.rate) && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200">
                      <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-purple-600" />
                        Tier Summary
                      </h5>
                      <div className="space-y-1">
                        {variableRateTiers
                          .filter(t => t.min_value !== '' && t.max_value !== '' && t.rate !== '')
                          .map((tier, index) => (
                            <p key={index} className="text-sm text-gray-600">
                              {formatCurrency(tier.min_value)} - {formatCurrency(tier.max_value)}:{' '}
                              <span className="font-semibold text-purple-700">{tier.rate}%</span>
                            </p>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Other Fields Grid */}
                        <div className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <div className="grid grid-cols-2 gap-6">
              {inputFields.map((field) => (
                <div key={field.key} className="pb-4">
                  <label className="block font-bold text-purple-900 mb-1">{field.label}</label>
                  <p className="text-sm text-gray-500 mb-2">{field.description}</p>
                  <div className="flex items-stretch">
                    {field.prefix && (
                      <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 flex items-center rounded-l-md text-gray-600">
                        {field.prefix}
                      </span>
                    )}
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`border border-gray-300 px-3 py-2 w-full ${
                        field.prefix ? '' : 'rounded-l-md'
                      } ${field.suffix ? '' : 'rounded-r-md'}`}
                      placeholder={field.placeholder}
                      value={formData[field.key]}
                      onChange={(e) => handleInputChange(field.key, e.target.value)}
                    />
                    {field.suffix && (
                      <span className="bg-gray-100 border border-l-0 border-gray-300 px-3 flex items-center rounded-r-md text-gray-600">
                        {field.suffix}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              
            </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default BillingSettings;
